import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption } from "@vengreso/angular-components";
import { RxwebValidators } from "@rxweb/reactive-form-validators";
import { distinctUntilChanged } from "rxjs/operators";

import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { CategoryRenameComponent } from "../../shortcuts/add-shortcut-category/category-rename/category-rename.component";
import { AddShortcutComponent } from "../../shortcuts/add-shortcut/add-shortcut.component";
import { CategorySelectComponent } from "../../shared/category-select/category-select.component";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { DynamicStorage } from "src/app/core/core-services/decorators/dynamic-storage";

@Component({
    selector: "app-add-to-flycuts",
    templateUrl: "./add-to-flycuts.component.html",
    standalone: false
})
export class AddToFlycutsComponent implements OnInit {
  public form: FormGroup;
  @DynamicStorage(SHORTCUT_CATEGORIES) shortcutCategoryList: any[];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true".
  // categoryOptions is the flattened, data-driven option list (built once per
  // shortcutCategoryList change) — vng-select's own built-in substring search
  // now does the filtering that filterCtrl/filterCategories() used to do by
  // hand (cheatsheet #22).
  categoryOptions: VngSelectOption[] = [];
  @ViewChild("selectElem") selectElem: CategorySelectComponent;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dataService: DataService,
    private readonly notification: NotificationService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<AddShortcutComponent>,
    public dialog: DialogService,
    public extensionService: ExtensionService,
    private readonly sharedData: SharedDataService,
    private readonly shortcutCategoryService: ShortcutCategoryService
  ) {}

  ngOnInit(): void {
    this.getShortcutCategory();

    this.form = this.fb.group({
      category_id: [null, [RxwebValidators.required()]],
    });

    this.sharedData.storage
      .observe(SHORTCUT_CATEGORIES)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((value) => {
        this.shortcutCategoryList = value;
        this.buildCategoryOptions();
      });

    this.buildCategoryOptions();
  }

  // R5: flatten the category tree into data-driven vng-select options once
  // per shortcutCategoryList change. Indentation uses NBSP (\u00A0 escapes
  // below) — plain spaces collapse in rendered HTML text, NBSP does not.
  buildCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.shortcutCategoryList || []) {
      options.push({ value: cat.id, label: cat.name });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name });
        for (const sub2 of sub1.sub_category_lv2 || []) {
          options.push({ value: sub2.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + sub2.name });
        }
      }
    }
    this.categoryOptions = options;
  }

  shortcutCategoryRename(data: any) {
    this.dialog.openDialogComponent(CategoryRenameComponent, {
      self: this,
      data,
      backdropClass: "dialog-category-backdrop",
    });
  }

  saveCategory(data) {
    this.dataService.post({ url: "v1/user/shortcut-category", data }).subscribe({
      next: (response: any) => {
        if (response?.result) {
          this.getShortcutCategory(true);
          this.extensionService.sendUpdateMessage();
          this.form.patchValue({
            category_id: response.result,
          });
          this.selectElem.closePanel();
          this.notification.toast("Category added successfully!");
        }
      },
      error: () => {},
    });
  }

  getShortcutCategory(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }

  close(): void {
    this.dialogRef.close();
  }

  getCategoryType(id: string) {
    for (const item of this.shortcutCategoryList) {
      if (item.id == id) {
        return "category";
      }
      if (item.sub_category_lv1) {
        for (const subLv1 of item.sub_category_lv1) {
          if (subLv1.id == id) {
            return "sub_categories_lv1";
          }
          if (subLv1.sub_category_lv2) {
            for (const subLv2 of subLv1.sub_category_lv2) {
              if (subLv2.id == id) {
                return "sub_categories_lv2";
              }
            }
          }
        }
      }
    }
  }

  addToShortcut(data: any) {
    data.type = this.getCategoryType(data.category_id);
    this.dataService.post({ url: `v1/shares-shortcut/${this.data.shortcut.id}`, data }).subscribe({
      next: (response: any) => {
        this.notification.toast("FlyShare added successfully.");
        this.data.self.init(this.data.self.activeRoute);
        this.close();
      },
      error: (error) => {
        this.notification.toast(error.error.error ?? "Something went wrong. Contact administrator");
      },
    });
  }
}
